import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  UserProfile,
  UserProgress,
  Achievement,
  UserSettings,
  LevelProgress,
  ChallengeProgress,
  RoomProgress,
} from '@/types/user.types';
import { usersApi } from '@/api/users';
import { progressApi } from '@/api/progress';
import { achievementsApi } from '@/api/achievements';
import {
  apiProfileToLocal,
  apiTutorialProgressToLocal,
  apiChallengeProgressToLocal,
  apiRoomProgressToLocal,
  apiAchievementToLocal,
} from '@/utils/adapters';
import { calculateLevel } from '@/utils/levelCalculator';

interface UserState {
  profile: UserProfile;
  progress: UserProgress;
  achievements: Achievement[];
  settings: UserSettings;
  isLoading: boolean;
  error: string | null;
  isBackendEnabled: boolean;

  // Local-only actions (for backward compatibility)
  updateProgress: (levelId: string, progress: Partial<LevelProgress>) => void;
  unlockAchievement: (achievementId: string) => void;
  addXP: (amount: number) => void;
  updateSettings: (settings: Partial<UserSettings>) => void;

  // Backend sync actions
  enableBackend: () => void;
  disableBackend: () => void;
  clearProfile: () => void;
  setProfileId: (id: string, username?: string) => void;
  fetchProfile: () => Promise<void>;
  fetchProgress: () => Promise<void>;
  fetchAchievements: () => Promise<void>;
  fetchSettings: () => Promise<void>;
  syncProfile: (updates: Partial<UserProfile>) => Promise<void>;
  syncXP: (amount: number, source?: string) => Promise<void>;
  syncTutorialProgress: (levelId: string, progress: Partial<LevelProgress>) => Promise<void>;
  syncChallengeProgress: (challengeId: string, progress: Partial<ChallengeProgress>) => Promise<void>;
  syncRoomProgress: (roomId: string, progress: Partial<RoomProgress>) => Promise<void>;
  syncSettings: (settings: Partial<UserSettings>) => Promise<void>;
  syncUnlockAchievement: (achievementId: string) => Promise<void>;
  setError: (error: string | null) => void;
  recalculateLevel: () => void;
}

const defaultProfile: UserProfile = {
  id: crypto.randomUUID(),
  xp: 0,
  level: 1,
  createdAt: new Date(),
  lastActive: new Date(),
};

const defaultProgress: UserProgress = {
  tutorialProgress: [],
  challengeProgress: [],
  sandboxSessions: [],
  escapeRoomProgress: [],
  achievements: [],
};

const defaultSettings: UserSettings = {
  theme: 'light',
  soundEnabled: true,
  musicEnabled: true,
  animationsEnabled: true,
};

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      profile: defaultProfile,
      progress: defaultProgress,
      achievements: [],
      settings: defaultSettings,
      isLoading: false,
      error: null,
      isBackendEnabled: false,

      // Backend control
      enableBackend: () => set({ isBackendEnabled: true }),
      disableBackend: () => set({ isBackendEnabled: false }),
      clearProfile: () => set({
        profile: {
          id: crypto.randomUUID(),
          xp: 0,
          level: 1,
          createdAt: new Date(),
          lastActive: new Date(),
        },
        progress: defaultProgress,
        achievements: [],
        settings: defaultSettings,
        isBackendEnabled: false,
      }),
      setProfileId: (id: string, username?: string) => set((state) => ({
        profile: {
          ...state.profile,
          id,
          ...(username && { username }),
        }
      })),
      setError: (error) => set({ error }),

      // Fetch methods
      fetchProfile: async () => {
        const state = get();
        if (!state.isBackendEnabled) return;

        set({ isLoading: true, error: null });
        try {
          const apiProfile = await usersApi.getProfile(state.profile.id);
          const profile = apiProfileToLocal(apiProfile);
          set({ profile, isLoading: false });
        } catch (error: any) {
          console.error('Failed to fetch profile:', error);
          set({ error: error.message, isLoading: false });
        }
      },

      fetchProgress: async () => {
        const state = get();
        if (!state.isBackendEnabled) return;

        set({ isLoading: true, error: null });
        try {
          const apiProgress = await progressApi.getAllProgress(state.profile.id);

          const tutorialProgress = apiProgress.tutorialProgress.map(apiTutorialProgressToLocal);
          const challengeProgress = apiProgress.challengeProgress.map(apiChallengeProgressToLocal);
          const escapeRoomProgress = apiProgress.escapeRoomProgress.map(apiRoomProgressToLocal);

          set({
            progress: {
              ...state.progress,
              tutorialProgress,
              challengeProgress,
              escapeRoomProgress,
            },
            isLoading: false,
          });
        } catch (error: any) {
          console.error('Failed to fetch progress:', error);
          set({ error: error.message, isLoading: false });
        }
      },

      fetchAchievements: async () => {
        const state = get();
        if (!state.isBackendEnabled) return;

        set({ isLoading: true, error: null });
        try {
          const response = await achievementsApi.getAchievements(state.profile.id);
          const achievements = response.achievements.map(apiAchievementToLocal);
          set({ achievements, isLoading: false });
        } catch (error: any) {
          console.error('Failed to fetch achievements:', error);
          set({ error: error.message, isLoading: false });
        }
      },

      fetchSettings: async () => {
        const state = get();
        if (!state.isBackendEnabled) return;

        set({ isLoading: true, error: null });
        try {
          const settings = await usersApi.getSettings(state.profile.id);
          set({ settings, isLoading: false });
        } catch (error: any) {
          console.error('Failed to fetch settings:', error);
          set({ error: error.message, isLoading: false });
        }
      },

      // Sync methods (optimistic updates)
      syncProfile: async (updates) => {
        const state = get();
        if (!state.isBackendEnabled) {
          // Fallback to local update
          set((state) => ({ profile: { ...state.profile, ...updates } }));
          return;
        }

        // Optimistic update
        set((state) => ({ profile: { ...state.profile, ...updates }, error: null }));

        try {
          const apiProfile = await usersApi.updateProfile(state.profile.id, {
            username: updates.username,
          });
          const profile = apiProfileToLocal(apiProfile);
          set({ profile });
        } catch (error: any) {
          console.error('Failed to sync profile:', error);
          set({ error: error.message });
          // Revert on error
          await get().fetchProfile();
        }
      },

      syncXP: async (amount, source) => {
        const state = get();
        if (!state.isBackendEnabled) {
          // Fallback to local update with level calculation
          const newXP = state.profile.xp + amount;
          const newLevel = calculateLevel(newXP);
          set((state) => ({
            profile: {
              ...state.profile,
              xp: newXP,
              level: newLevel,
            },
          }));
          return;
        }

        // Optimistic update with level calculation
        const newXP = state.profile.xp + amount;
        const newLevel = calculateLevel(newXP);
        set((state) => ({
          profile: {
            ...state.profile,
            xp: newXP,
            level: newLevel,
          },
          error: null,
        }));

        try {
          const response = await usersApi.addXP(state.profile.id, { amount, source });
          set((state) => ({
            profile: {
              ...state.profile,
              xp: response.xp,
              level: response.level,
            },
          }));
        } catch (error: any) {
          console.error('Failed to sync XP:', error);
          set({ error: error.message });
          // Revert on error
          await get().fetchProfile();
        }
      },

      syncTutorialProgress: async (levelId, progressUpdate) => {
        const state = get();

        // Always update locally first
        const existingIndex = state.progress.tutorialProgress.findIndex(
          (p) => p.levelId === levelId
        );

        const newProgress = [...state.progress.tutorialProgress];
        if (existingIndex >= 0) {
          newProgress[existingIndex] = {
            ...newProgress[existingIndex],
            ...progressUpdate,
          };
        } else {
          newProgress.push({
            levelId,
            completed: false,
            stars: 0,
            attempts: 0,
            ...progressUpdate,
          });
        }

        set({
          progress: {
            ...state.progress,
            tutorialProgress: newProgress,
          },
          error: null,
        });

        // Sync to backend if enabled
        if (state.isBackendEnabled) {
          try {
            const current = newProgress[existingIndex >= 0 ? existingIndex : newProgress.length - 1];
            await progressApi.updateTutorialProgress(state.profile.id, levelId, {
              completed: current.completed,
              stars: current.stars,
              attempts: current.attempts,
            });
          } catch (error: any) {
            console.error('Failed to sync tutorial progress:', error);
            set({ error: error.message });
          }
        }
      },

      syncChallengeProgress: async (challengeId, progressUpdate) => {
        const state = get();

        const existingIndex = state.progress.challengeProgress.findIndex(
          (p) => p.challengeId === challengeId
        );

        const newProgress = [...state.progress.challengeProgress];
        if (existingIndex >= 0) {
          newProgress[existingIndex] = {
            ...newProgress[existingIndex],
            ...progressUpdate,
          };
        } else {
          newProgress.push({
            challengeId,
            completed: false,
            bestScore: 0,
            bestTime: 0,
            attempts: 0,
            ...progressUpdate,
          });
        }

        set({
          progress: {
            ...state.progress,
            challengeProgress: newProgress,
          },
          error: null,
        });

        if (state.isBackendEnabled) {
          try {
            const current = newProgress[existingIndex >= 0 ? existingIndex : newProgress.length - 1];
            await progressApi.updateChallengeProgress(state.profile.id, challengeId, {
              completed: current.completed,
              score: current.bestScore,
              time: current.bestTime,
              attempts: current.attempts,
            });
          } catch (error: any) {
            console.error('Failed to sync challenge progress:', error);
            set({ error: error.message });
          }
        }
      },

      syncRoomProgress: async (roomId, progressUpdate) => {
        const state = get();

        const existingIndex = state.progress.escapeRoomProgress.findIndex(
          (p) => p.roomId === roomId
        );

        const newProgress = [...state.progress.escapeRoomProgress];
        if (existingIndex >= 0) {
          newProgress[existingIndex] = {
            ...newProgress[existingIndex],
            ...progressUpdate,
          };
        } else {
          newProgress.push({
            roomId,
            completed: false,
            ...progressUpdate,
          });
        }

        set({
          progress: {
            ...state.progress,
            escapeRoomProgress: newProgress,
          },
          error: null,
        });

        if (state.isBackendEnabled) {
          try {
            const current = newProgress[existingIndex >= 0 ? existingIndex : newProgress.length - 1];
            await progressApi.updateRoomProgress(state.profile.id, roomId, {
              completed: current.completed,
              time: current.bestTime,
              puzzlesCompleted: progressUpdate.puzzlesCompleted || 0,
              attempts: 1,
            });
          } catch (error: any) {
            console.error('Failed to sync room progress:', error);
            set({ error: error.message });
          }
        }
      },

      syncSettings: async (newSettings) => {
        const state = get();

        // Optimistic update
        set((state) => ({
          settings: {
            ...state.settings,
            ...newSettings,
          },
          error: null,
        }));

        if (state.isBackendEnabled) {
          try {
            await usersApi.updateSettings(state.profile.id, newSettings);
          } catch (error: any) {
            console.error('Failed to sync settings:', error);
            set({ error: error.message });
            // Revert on error
            await get().fetchSettings();
          }
        }
      },

      syncUnlockAchievement: async (achievementId) => {
        const state = get();

        // Check if already unlocked
        if (state.achievements.some((a) => a.id === achievementId)) {
          return;
        }

        if (state.isBackendEnabled) {
          try {
            const apiAchievement = await achievementsApi.unlockAchievement(state.profile.id, achievementId);
            const achievement = apiAchievementToLocal(apiAchievement);
            set((state) => ({
              achievements: [...state.achievements, achievement],
              error: null,
            }));
          } catch (error: any) {
            console.error('Failed to unlock achievement:', error);
            set({ error: error.message });
          }
        } else {
          // Fallback to local unlock
          const achievement = {
            id: achievementId,
            name: achievementId,
            description: '',
            icon: '',
            unlockedAt: new Date(),
            rarity: 'common' as const,
          };
          set((state) => ({
            achievements: [...state.achievements, achievement],
          }));
        }
      },

      // Legacy methods for backward compatibility
      updateProgress: (levelId, progressUpdate) => {
        get().syncTutorialProgress(levelId, progressUpdate);
      },

      unlockAchievement: (achievementId) => {
        get().syncUnlockAchievement(achievementId);
      },

      addXP: (amount) => {
        get().syncXP(amount);
      },

      updateSettings: (newSettings) => {
        get().syncSettings(newSettings);
      },

      // Recalculate level from current XP
      recalculateLevel: () => {
        const state = get();
        const correctLevel = calculateLevel(state.profile.xp);
        if (state.profile.level !== correctLevel) {
          console.log(`🔧 Fixing level: ${state.profile.level} → ${correctLevel} (XP: ${state.profile.xp})`);
          set((state) => ({
            profile: {
              ...state.profile,
              level: correctLevel,
            },
          }));
        }
      },
    }),
    {
      name: 'codequest_user_v1',
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error('Failed to rehydrate storage:', error);
            return;
          }
          // Schedule level recalculation after hydration completes
          if (state) {
            setTimeout(() => {
              state.recalculateLevel();
            }, 0);
          }
        };
      },
    }
  )
);
